/**
 * schema.js — constants, enumerations, and alias helpers for the System of Work domain.
 * Pure: no imports, no state, no React.
 */

// ---------------------------------------------------------------------------
// Cell status vocabulary
// ---------------------------------------------------------------------------

/** All valid cell statuses, in lifecycle order. */
export const STATUSES = [
  "draft",
  "planned",
  "ready",
  "working",
  "input_needed",
  "completed",
  "failed"
];

/** Terminal statuses — once reached, a cell does not progress further. */
export const TERMINAL_STATUSES = ["completed", "failed"];

/** Non-terminal statuses — cell is still live. */
export const NONTERMINAL_STATUSES = ["draft", "planned", "ready", "working", "input_needed"];

/**
 * Returns the CSS class name for a status badge pill.
 * Maps to the app-global outlined pill classes (title-tag-badge / title-tag-badge-warning).
 * @param {string} status
 * @returns {string}
 */
export function statusPillClass(status) {
  if (status === 'failed' || status === 'input_needed') return 'title-tag-badge-warning';
  return 'title-tag-badge';
}

// ---------------------------------------------------------------------------
// Step, agent, and permission vocabularies
// ---------------------------------------------------------------------------

/** Valid cell-workflow step types. */
export const STEP_TYPES = ["worker_task", "external_input", "workspace_init"];

/** Valid external_input request kinds. */
export const REQUEST_KINDS = ["approval", "choice", "question", "review"];

/** Valid agent types for worker_task steps. */
export const AGENT_TYPES = ["claude_code", "codex"];

/** Valid permission modes for worker_task agent blocks. */
export const PERMISSION_MODES = ["approval", "autonomous"];

// ---------------------------------------------------------------------------
// Workspace vocabularies
// ---------------------------------------------------------------------------

/** Valid workspace types. */
export const WORKSPACE_TYPES = ["local_folder"];

/** Valid skill-pack sources. */
export const PACK_SOURCES = ["plugin", "git", "path"];

// ---------------------------------------------------------------------------
// Well-known session-object aliases
// ---------------------------------------------------------------------------

/** Alias for the cell-workflow template library session object. */
export const CELL_WORKFLOWS_ALIAS = "cell_workflows";

/** Alias for the workspace template library session object. */
export const WORKSPACES_ALIAS = "workspaces";

/** Alias for the heartbeat-state session object. */
export const HEARTBEAT_STATE_ALIAS = "heartbeat_state";

// ---------------------------------------------------------------------------
// Alias helpers
// ---------------------------------------------------------------------------

/**
 * Returns the session-graph alias for a mission's graph object.
 * @param {string} id - mission id
 * @returns {string} e.g. "mission:abc"
 */
export function missionAlias(id) {
  return `mission:${id}`;
}

/**
 * Returns true if the given alias is a mission graph alias.
 * @param {string} alias
 * @returns {boolean}
 */
export function isMissionAlias(alias) {
  return typeof alias === "string" && alias.startsWith("mission:");
}

/**
 * Returns the thread alias for a cell's meta-thread (the cell's record/context bus).
 * @param {string} cellId
 * @returns {string} e.g. "cell:abc"
 */
export function cellMetaThreadAlias(cellId) {
  return `cell:${cellId}`;
}

/**
 * Returns the thread alias for a specific step run within a cell.
 * @param {string} cellId
 * @param {string|number} step - step index or name
 * @param {string|number} run - run index or id
 * @returns {string} e.g. "cell:abc/0#1"
 */
export function stepThreadAlias(cellId, step, run) {
  return `cell:${cellId}/${step}#${run}`;
}

// ---------------------------------------------------------------------------
// Default value helpers
// ---------------------------------------------------------------------------

/**
 * Returns a default empty agent block.
 * @returns {{ type: string, model: string, effort: string, permissions: string, executable: string }}
 */
export function emptyAgent() {
  return { type: "", model: "", effort: "", permissions: "", executable: "" };
}
